import React, { useState, useRef, useEffect } from 'react';
import { Button, Row, Col, Card, Space, Select, Typography, message } from 'antd';

const { Option } = Select;
const { Title, Text } = Typography;

const PDFDebugger = () => {
  const canvasRef = useRef(null);
  const [selectedField, setSelectedField] = useState('borrowerName');
  const [coordinates, setCoordinates] = useState({
    borrowerName: { x: 156, y: 520, label: 'Borrower Name', text: 'John Doe' },
    borrowerNrc: { x: 184, y: 502, label: 'Borrower NRC', text: '123456/78/9' },
    borrowerOccupation: { x: 171, y: 484, label: 'Borrower Occupation', text: 'Engineer' },
    borrowerAddress: { x: 161, y: 466, label: 'Borrower Address', text: 'Lusaka, Zambia' },
    borrowerEmail: { x: 183, y: 448, label: 'Borrower Email', text: 'john@example.com' },
    borrowerPhone: { x: 156, y: 430, label: 'Borrower Phone', text: '+260123456789' },
    nextOfKinName: { x: 486, y: 520, label: 'Next of Kin Name', text: 'Jane Doe' },
    nextOfKinNrc: { x: 514, y: 502, label: 'Next of Kin NRC', text: '987654/32/1' },
    agreementDate: { x: 207, y: 364, label: 'Agreement Date', text: '21/08/2025' },
    repaymentDay: { x: 202, y: 346, label: 'Repayment Day', text: '21/09/2025' },
    loanPeriod: { x: 177, y: 328, label: 'Loan Period', text: '30 days' },
    collateral: { x: 467, y: 328, label: 'Collateral', text: 'Vehicle' },
  });

  const scale = 0.8; // Scale factor for display

  // Initialize canvas
  useEffect(() => {
    drawCanvas();
  }, [coordinates, selectedField]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Set canvas size (A4 proportions scaled down)
    canvas.width = 595 * scale;
    canvas.height = 842 * scale;
    
    // Clear and set white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw PDF outline
    ctx.strokeStyle = '#ddd';
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
    
    // Add some visual structure (representing PDF form areas)
    ctx.strokeStyle = '#eee';
    ctx.setLineDash([5, 5]);
    
    // Header area
    ctx.strokeRect(20 * scale, 20 * scale, (595 - 40) * scale, 100 * scale);
    
    // Main content area
    ctx.strokeRect(20 * scale, 140 * scale, (595 - 40) * scale, 500 * scale);
    
    // Footer area  
    ctx.strokeRect(20 * scale, 660 * scale, (595 - 40) * scale, 100 * scale);
    
    ctx.setLineDash([]);
    
    // Draw coordinate markers and text
    Object.entries(coordinates).forEach(([key, coord]) => {
      const x = coord.x * scale;
      const y = (842 - coord.y) * scale; // Flip Y coordinate for canvas
      
      // Draw marker circle
      ctx.fillStyle = key === selectedField ? '#ff4d4f' : '#1890ff';
      ctx.beginPath();
      ctx.arc(x, y, key === selectedField ? 6 : 4, 0, 2 * Math.PI);
      ctx.fill();
      
      // Draw crosshair for selected field
      if (key === selectedField) {
        ctx.strokeStyle = '#ff4d4f';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x - 10, y);
        ctx.lineTo(x + 10, y);
        ctx.moveTo(x, y - 10);
        ctx.lineTo(x, y + 10);
        ctx.stroke();
      }
      
      // Draw sample text
      ctx.fillStyle = key === selectedField ? '#000' : '#666';
      ctx.font = `${9 * scale}px Arial`;
      ctx.fillText(coord.text, x, y + 15 * scale);
      
      // Draw label for selected field
      if (key === selectedField) {
        ctx.fillStyle = '#ff4d4f';
        ctx.font = `${10 * scale}px Arial`;
        ctx.fillText(coord.label, x + 15 * scale, y - 10 * scale);
        
        // Show coordinates
        ctx.fillStyle = '#666';
        ctx.font = `${8 * scale}px Arial`;
        ctx.fillText(`(${coord.x}, ${coord.y})`, x + 15 * scale, y + 5 * scale);
      }
    });
  };

  // Handle canvas click
  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const clickX = Math.round((event.clientX - rect.left) / scale);
    const clickY = Math.round(842 - (event.clientY - rect.top) / scale); // Flip Y for PDF coordinates
    
    // Update selected field coordinates
    setCoordinates(prev => ({
      ...prev,
      [selectedField]: {
        ...prev[selectedField],
        x: clickX,
        y: clickY
      }
    }));
    
    message.success(`${coordinates[selectedField].label} positioned at (${clickX}, ${clickY})`);
  };

  // Export coordinates
  const exportCoordinates = () => {
    const coordString = Object.entries(coordinates)
      .map(([field, coord]) => `// ${coord.label}\ndrawText(secondPage, data, { x: ${coord.x}, y: ${coord.y}, size: 9 });`)
      .join('\n\n');
    
    navigator.clipboard.writeText(coordString).then(() => {
      message.success('Coordinates copied to clipboard!');
    });
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Title level={2}>Interactive PDF Coordinate Editor</Title>
      
      <Row gutter={[24, 24]}>
        <Col span={16}>
          <Card 
            title="PDF Template - Click to Position Fields" 
            style={{ height: 'fit-content' }}
          >
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <canvas
                ref={canvasRef}
                onClick={handleCanvasClick}
                style={{ 
                  border: '2px solid #d9d9d9', 
                  cursor: 'crosshair',
                  backgroundColor: 'white'
                }}
              />
            </div>
            <Text type="secondary">
              Click anywhere on the canvas to position the selected field. 
              Red dot = selected field, Blue dots = other fields.
            </Text>
          </Card>
        </Col>
        
        <Col span={8}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            
            <Card title="Field Selection">
              <Select
                value={selectedField}
                onChange={setSelectedField}
                style={{ width: '100%' }}
                placeholder="Select field to position"
              >
                {Object.entries(coordinates).map(([key, coord]) => (
                  <Option key={key} value={key}>
                    {coord.label}
                  </Option>
                ))}
              </Select>
              <div style={{ marginTop: '16px' }}>
                <Text strong>Current Position:</Text><br />
                <Text code>
                  X: {coordinates[selectedField]?.x}, Y: {coordinates[selectedField]?.y}
                </Text>
              </div>
            </Card>

            <Card title="Actions">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  onClick={exportCoordinates}
                  block
                >
                  📋 Export Coordinates
                </Button>
                <Button 
                  onClick={() => window.open('/pdf-debug', '_blank')}
                  block
                >
                  🔄 Reset View
                </Button>
              </Space>
            </Card>

            <Card title="Instructions" size="small">
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Select a field from the dropdown</li>
                <li>Click on the canvas where you want to position it</li>
                <li>Repeat for all fields</li>
                <li>Click "Export Coordinates" to copy the code</li>
                <li>Paste into your pdfService.js file</li>
              </ol>
            </Card>

          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default PDFDebugger;